import { useEffect, useRef, useState } from 'react'
import { Button } from '@arcanecircle/ui/grimoire/button'
import { IconButton } from '@arcanecircle/ui/grimoire/icon-button'
import { Input } from '@arcanecircle/ui/ui/input'
import { NativeSelect } from '@arcanecircle/ui/ui/native-select'
import { Switch } from '@arcanecircle/ui/ui/switch'
import { socketService } from '@/services/socket'
import {
  createNpcCharacter,
  fetchCharacterVTTData,
  uploadTokenImage,
  getCharacterCreationUrl,
  updateCharacterStats,
  type TokenClassification,
} from '@/services/characterService'
import { fetchSessionMeta, getCachedSessionMeta } from '@/services/sessionMetaService'
import { useVTTStore } from '@/store'

/** Fallback system for NPCs created in a session whose game system is
 *  unknown (metadata never loaded, or the platform left it blank). */
const FALLBACK_SYSTEM = 'generic'

interface NpcTokenModalProps {
  authToken: string
  /** The VTT session id — used to resolve the tabletop's game system so
   *  created NPCs match the system the game is being played in. */
  sessionId: string
  onClose: () => void
  /**
   * Edit an existing token's stats instead of creating a new one (#657).
   * Supplying this switches the modal from "New NPC Token" into
   * "Edit Token Stats" mode: HP current/max, AC, and initiative are
   * pre-filled from the character's current sheet and PATCHed on submit.
   * Name, classification, image, and mob-status aren't touched here — the
   * token tooltip already has dedicated controls for those.
   */
  editTarget?: { characterId: string; name: string }
}

const VTT_API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'

/**
 * GM-only modal for creating NPC tokens with lightweight stat blocks, and
 * (with `editTarget`) for correcting an existing token's HP max / AC /
 * initiative after it's already been dropped on the map (#657).
 *
 * Create mode: fills a form, creates a character on the platform, spawns
 * the token. Linking an already-created NPC is no longer done here — the
 * Tokens sidebar lists every created NPC and offers a Place action (VTT
 * #163).
 *
 * No new VTT-DB columns. Character data lives on ac-mvp; the token just
 * holds a characterId that the server resolves on demand.
 */
export function NpcTokenModal({ authToken, sessionId, onClose, editTarget }: NpcTokenModalProps) {
  const isEdit = !!editTarget
  // Backdrop dismiss must fire only on a genuine outside click — a press AND
  // release both on the backdrop. Relying on the click event alone closes the
  // modal when an inner control re-renders mid-click: the mousedown target
  // unmounts, the browser reparents the click to the backdrop, and onClose
  // fires. Track where the press started instead.
  const pressStartedOnBackdrop = useRef(false)

  // Resolve the tabletop's game system so created NPCs use the same
  // system the game is played in. Session metadata is normally cached
  // (fetched at app launch); seed from the cache, then refresh in case
  // the modal opens before that fetch completed. Falls back to generic.
  const [gameSystem, setGameSystem] = useState<string>(
    () => getCachedSessionMeta(sessionId)?.gameSystem || FALLBACK_SYSTEM,
  )

  // ── create mode fields ──────────────────────────────────────────────────
  const [name, setName] = useState('')
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [classification, setClassification] = useState<TokenClassification>('enemy')
  const [speed, setSpeed] = useState(30)
  const [isMob, setIsMob] = useState(false)

  // ── stats fields — shared by create and edit modes (#657 adds ac/initiative
  // to both; edit mode reuses HP current/max from the create form too). ──────
  const [hpCurrent, setHpCurrent] = useState(10)
  const [hpMax, setHpMax] = useState(10)
  const [ac, setAc] = useState<number | ''>('')
  const [initiative, setInitiative] = useState<number | ''>('')

  // ── edit mode ────────────────────────────────────────────────────────────
  const [loadingStats, setLoadingStats] = useState(isEdit)

  // ── shared ──────────────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { activeMapId } = useVTTStore()

  // Prefill from the character's current sheet when editing.
  useEffect(() => {
    if (!editTarget) return
    let cancelled = false
    setLoadingStats(true)
    fetchCharacterVTTData(editTarget.characterId, authToken, { bypassCache: true })
      .then((data) => {
        if (cancelled) return
        if (!data) {
          setError('Failed to load current stats')
          return
        }
        // The wire format always computes a value for d20 sheets (AC
        // defaults to 0, initiative to the DEX modifier) even when the token
        // has never had them set — that computed value IS what's currently
        // shown to players in the tooltip, so it's the right prefill.
        setHpCurrent(data.stats.hp.current)
        setHpMax(Math.max(1, data.stats.hp.max))
        setAc(data.stats.ac)
        setInitiative(data.stats.initiative)
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load current stats')
      })
      .finally(() => {
        if (!cancelled) setLoadingStats(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editTarget?.characterId, authToken])

  // Escape key closes modal
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  // Ensure the game system is resolved even if the modal opens before
  // the app-level session-meta fetch lands. Cheap (cached) on repeat.
  useEffect(() => {
    if (!sessionId || !authToken) return
    let cancelled = false
    fetchSessionMeta(sessionId, authToken)
      .then((meta) => {
        if (!cancelled && meta?.gameSystem) setGameSystem(meta.gameSystem)
      })
      .catch(() => {
        /* keep the cached/fallback system */
      })
    return () => {
      cancelled = true
    }
  }, [sessionId, authToken])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null
    if (!file) { setImageFile(null); return }
    if (!file.type.startsWith('image/')) { setError('Select an image file'); return }
    if (file.size > 10 * 1024 * 1024) { setError('Image must be under 10 MB'); return }
    setImageFile(file)
    setError(null)
  }

  const spawnToken = (
    characterId: string,
    displayName: string,
    imageUrl?: string | null,
    ownerUserId?: string | null,
    seedInstanceStats?: { hp: { current: number; max: number; temp: number }; statuses: string[] } | null,
  ) => {
    if (!activeMapId) { setError('No active map'); return }
    socketService.createToken({
      mapId: activeMapId,
      name: displayName,
      x: 1,
      y: 1,
      characterId,
      imageUrl: imageUrl ?? undefined,
      // Owner so the character's player can move their own token (canMoveToken).
      ownerUserId: ownerUserId ?? undefined,
      // Mob tokens start with independent per-token HP seeded from the statblock.
      instanceStats: seedInstanceStats ?? null,
    })
  }

  // ── create mode submit ──────────────────────────────────────────────────
  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) { setError('Name is required'); return }
    if (hpMax <= 0) { setError('HP max must be > 0'); return }
    setSubmitting(true)
    setError(null)

    try {
      // 1. Upload image if provided
      let imageUrl: string | null = null
      if (imageFile) {
        imageUrl = await uploadTokenImage(imageFile, VTT_API_URL, authToken)
        if (!imageUrl) { setError('Image upload failed'); setSubmitting(false); return }
      }

      // 2. Create the character on the platform
      const characterId = await createNpcCharacter(
        {
          name: name.trim(),
          system: gameSystem || FALLBACK_SYSTEM,
          imageUrl,
          tokenClassification: classification,
          isMob,
          derivedStats: {
            hp: { current: hpCurrent, max: hpMax },
            speed,
            // Omit entirely when left blank, same as before AC/initiative existed.
            ...(ac !== '' ? { ac } : {}),
            ...(initiative !== '' ? { initiative } : {}),
          },
        },
        authToken
      )

      if (!characterId) {
        setError('Failed to create character on platform. The endpoint may not be deployed yet — token creation requires the ac-mvp foundation build (PR #1 of 2).')
        setSubmitting(false)
        return
      }

      // 3. Prime the character cache so the sidebar / HUD render with
      //    real stats the moment the token broadcast lands. The
      //    WebSocket layer would otherwise trigger this fetch a tick
      //    later via onCharacterNeedsRefresh, briefly showing an empty
      //    HP bar in between.
      try {
        await fetchCharacterVTTData(characterId, authToken, {
          bypassCache: true,
        })
      } catch (err) {
        console.warn('[NpcTokenModal] cache-prime fetch failed', err)
      }

      // 4. Spawn the token on the map.
      // Mob tokens get instanceStats seeded from the statblock HP so each
      // placed token tracks damage independently from the first click.
      const seedInstanceStats = isMob
        ? { hp: { current: hpMax, max: hpMax, temp: 0 }, statuses: [] }
        : null
      spawnToken(characterId, name.trim(), imageUrl, undefined, seedInstanceStats)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setSubmitting(false)
    }
  }

  // ── edit mode submit ────────────────────────────────────────────────────
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editTarget) return
    if (hpMax <= 0) { setError('HP max must be > 0'); return }
    setSubmitting(true)
    setError(null)

    try {
      const res = await updateCharacterStats(
        editTarget.characterId,
        {
          set: hpCurrent,
          hpMax,
          ...(ac !== '' ? { ac } : {}),
          ...(initiative !== '' ? { initiative } : {}),
          // #766 — this modal is exactly the "GM's own unlinked NPC/test
          // token" case the platform-side fix added session-scoped GM
          // verification for. Without sessionId here, that fix is inert:
          // the platform has nothing to check the requester's GM-ness
          // against, and this GM keeps 403'ing on their own token.
          sessionId,
        },
        authToken
      )

      if (!res) {
        setError('Failed to save. Only the GM can edit a token’s stats.')
        setSubmitting(false)
        return
      }

      // Signal the session so every client (including this one's other
      // surfaces — InitiativePanel's roll modifier, the canvas HUD) refetches
      // the character and picks up the new values. Mirrors handleHpEdit's
      // broadcast in App.tsx for the #135 current-HP path. Guarded the same
      // way App.tsx guards it (wsStatus.connected) — emitCharacterHpChanged
      // throws if the socket isn't up.
      if (socketService.isConnected()) {
        socketService.emitCharacterHpChanged(editTarget.characterId)
      }
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setSubmitting(false)
    }
  }

  const createSheetUrl = getCharacterCreationUrl()

  return (
    <div
      className="vtt-modal-backdrop"
      onMouseDown={(e) => { pressStartedOnBackdrop.current = e.target === e.currentTarget }}
      onMouseUp={(e) => {
        if (pressStartedOnBackdrop.current && e.target === e.currentTarget) onClose()
        pressStartedOnBackdrop.current = false
      }}
    >
      <div
        className="vtt-modal-surface"
        onMouseDown={(e) => e.stopPropagation()}
        style={{ maxWidth: 480 }}
      >
        {/* Header */}
        <div className="vtt-modal-header">
          <div>
            <div className="ac-panel-eyebrow">GM · Token</div>
            <div className="ac-panel-title">
              {isEdit ? `Edit stats — ${editTarget!.name}` : 'New NPC Token'}
            </div>
          </div>
          <IconButton
            size="sm"
            variant="ghost"
            aria-label="Close"
            onClick={onClose}
          >
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </IconButton>
        </div>

        {isEdit ? (
          <form onSubmit={handleEditSubmit}>
            <div className="vtt-modal-body">
              {error && <ErrorBanner>{error}</ErrorBanner>}

              {loadingStats ? (
                <div className="ac-help" style={{ textAlign: 'center', padding: '1rem 0' }}>
                  Loading current stats…
                </div>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                    <div>
                      <label className="ac-label">HP current</label>
                      <Input
                        type="number"
                        value={hpCurrent}
                        onChange={(e) => setHpCurrent(parseInt(e.target.value) || 0)}
                        min={0}
                        required
                      />
                    </div>
                    <div>
                      <label className="ac-label">HP max</label>
                      <Input
                        type="number"
                        value={hpMax}
                        onChange={(e) => setHpMax(parseInt(e.target.value) || 1)}
                        min={1}
                        required
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                    <div>
                      <label className="ac-label">AC</label>
                      <Input
                        type="number"
                        value={ac}
                        onChange={(e) => setAc(e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                        min={0}
                      />
                    </div>
                    <div>
                      <label className="ac-label">Initiative</label>
                      <Input
                        type="number"
                        value={initiative}
                        onChange={(e) => setInitiative(e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                        min={0}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="vtt-modal-footer">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={submitting || loadingStats}
              >
                {submitting ? 'Saving…' : 'Save stats'}
              </Button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleCreateSubmit}>
            <div className="vtt-modal-body">
              {error && <ErrorBanner>{error}</ErrorBanner>}

              <div>
                <label className="ac-label">Name</label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Guard Captain"
                  required
                />
              </div>

              <div>
                <label className="ac-label">Classification</label>
                <NativeSelect
                  value={classification}
                  onChange={(e) => setClassification(e.target.value as TokenClassification)}
                >
                  <option value="enemy">Enemy NPC</option>
                  <option value="friendly">Friendly NPC</option>
                  <option value="player">Player-controlled</option>
                </NativeSelect>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
                <div>
                  <label className="ac-label">HP current</label>
                  <Input
                    type="number"
                    value={hpCurrent}
                    onChange={(e) => setHpCurrent(parseInt(e.target.value) || 0)}
                    min={0}
                    required
                  />
                </div>
                <div>
                  <label className="ac-label">HP max</label>
                  <Input
                    type="number"
                    value={hpMax}
                    onChange={(e) => setHpMax(parseInt(e.target.value) || 1)}
                    min={1}
                    required
                  />
                </div>
                <div>
                  <label className="ac-label">Speed (ft)</label>
                  <Input
                    type="number"
                    value={speed}
                    onChange={(e) => setSpeed(parseInt(e.target.value) || 0)}
                    min={0}
                    step={5}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                <div>
                  <label className="ac-label">AC (optional)</label>
                  <Input
                    type="number"
                    value={ac}
                    onChange={(e) => setAc(e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                    min={0}
                  />
                </div>
                <div>
                  <label className="ac-label">Initiative (optional)</label>
                  <Input
                    type="number"
                    value={initiative}
                    onChange={(e) => setInitiative(e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                    min={0}
                  />
                </div>
              </div>

              <Switch checked={isMob} onCheckedChange={setIsMob}>
                <div>
                  <span>Mob (independent tokens)</span>
                  <div
                    className="ac-help"
                    style={{ fontSize: '0.625rem', marginTop: 2, lineHeight: 1.4 }}
                  >
                    Each placed token tracks HP independently. Ctrl+V pastes copies, each at full HP.
                  </div>
                </div>
              </Switch>

              <div>
                <label className="ac-label">Token image (optional)</label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ padding: '0.375rem 0.75rem', height: 'auto' }}
                />
                {imageFile && (
                  <div className="ac-help" style={{ marginTop: '0.375rem', color: 'hsl(var(--ac-success))' }}>
                    {imageFile.name}
                  </div>
                )}
              </div>

              <div style={{ borderTop: '1px solid hsl(var(--ac-dark-accent))', paddingTop: '0.75rem' }}>
                <div className="ac-help" style={{ marginBottom: '0.375rem' }}>
                  Want a full character sheet?
                </div>
                <Button asChild variant="outline" size="sm">
                  <a
                    href={createSheetUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Create full sheet →
                  </a>
                </Button>
              </div>
            </div>

            <div className="vtt-modal-footer">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={submitting || !activeMapId}
              >
                {submitting ? 'Creating…' : 'Spawn token'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

function ErrorBanner({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: '0.625rem 0.75rem',
        background: 'hsl(var(--ac-danger) / 0.15)',
        border: '1px solid hsl(var(--ac-danger) / 0.4)',
        color: 'hsl(var(--ac-danger))',
        borderRadius: 'var(--ac-radius)',
        fontSize: '0.75rem',
      }}
    >
      {children}
    </div>
  )
}
